import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { loginApi, refreshTokenApi } from "./api";
import { tokenStorage } from "./tokenStorage";
import { jwtService } from "./jwt";

interface User {
  username: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  mustChangePassword: boolean;
  login: (username: string, password: string) => Promise<{ mustChangePassword: boolean }>;
  logout: () => void;
  getAccessToken: () => string | null;
  clearMustChangePassword: () => void;
  applyTokens: (access: string, refresh: string) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const logout = useCallback(() => {
    tokenStorage.clearTokens();
    setUser(null);
    setMustChangePassword(false);
  }, []);

  const clearMustChangePassword = useCallback(() => {
    setMustChangePassword(false);
  }, []);

  const applyTokens = useCallback((access: string, refresh: string) => {
    tokenStorage.setTokens(access, refresh);
    const usernameFromToken = jwtService.getUsername(access);
    if (usernameFromToken) {
      setUser({ username: usernameFromToken });
    }
    setMustChangePassword(jwtService.getMustChangePassword(access));
  }, []);

  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    const refreshToken = tokenStorage.getRefreshToken();
    if (!refreshToken) return null;

    try {
      const data = await refreshTokenApi(refreshToken);
      const newAccessToken = data.access;
      const currentRefreshToken = tokenStorage.getRefreshToken();
      if (currentRefreshToken) {
        tokenStorage.setTokens(newAccessToken, currentRefreshToken);
      }
      return newAccessToken;
    } catch {
      logout();
      return null;
    }
  }, [logout]);

  const login = useCallback(async (username: string, password: string) => {
    const data = await loginApi(username, password);
    tokenStorage.setTokens(data.access, data.refresh);
    const usernameFromToken = jwtService.getUsername(data.access);
    if (usernameFromToken) {
      setUser({ username: usernameFromToken });
    }
    const mustChange = jwtService.getMustChangePassword(data.access);
    setMustChangePassword(mustChange);
    return { mustChangePassword: mustChange };
  }, []);

  // Initialize auth state from stored tokens
  useEffect(() => {
    const initAuth = async () => {
      const accessToken = tokenStorage.getAccessToken();

      if (!accessToken) {
        setIsLoading(false);
        return;
      }

      // Check if access token is expired
      if (jwtService.isExpired(accessToken)) {
        // Try to refresh
        const newToken = await refreshAccessToken();
        if (newToken) {
          const username = jwtService.getUsername(newToken);
          if (username) {
            setUser({ username });
          }
          setMustChangePassword(jwtService.getMustChangePassword(newToken));
        }
      } else {
        const username = jwtService.getUsername(accessToken);
        if (username) {
          setUser({ username });
        }
        setMustChangePassword(jwtService.getMustChangePassword(accessToken));
      }

      setIsLoading(false);
    };

    initAuth();
  }, [refreshAccessToken]);

  // Auto-refresh token before expiry (5 minute threshold)
  useEffect(() => {
    if (!user) return;

    const checkAndRefresh = async () => {
      const accessToken = tokenStorage.getAccessToken();
      if (accessToken && jwtService.isExpired(accessToken, 300)) {
        await refreshAccessToken();
      }
    };

    // Check every minute
    const interval = setInterval(checkAndRefresh, 60000);
    return () => clearInterval(interval);
  }, [user, refreshAccessToken]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    mustChangePassword,
    login,
    logout,
    getAccessToken: () => tokenStorage.getAccessToken(),
    clearMustChangePassword,
    applyTokens,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
